// Openline marketing landing — human-crafted senior design & engineering architecture.
// Designed for authentic editorial authority, high conversion, and zero AI clichés.
// Dogfooding: `startChat` embeds + opens the real Openline widget (window.Openline.open).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Zap,
  UserCheck,
  Video,
  MonitorPlay,
  History,
  Paperclip,
  ShieldCheck,
  Users,
  Hand,
  Inbox,
  Sparkles,
  Route,
  Network,
  Share2,
  Clock,
  Palette,
  Cpu,
  Webhook,
  MessageSquare,
  SlidersHorizontal,
  Type as TypeIcon,
  MousePointerClick,
  MessageSquareText,
  Server,
  Lock,
  Globe,
  Sun,
  Moon,
  ArrowRight,
  ArrowUpRight,
  Check,
  Copy,
  Terminal,
  Shield,
  CheckCircle2,
  XCircle,
  Database,
  Smartphone,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { useAgentStore } from '../stores/agent';
import { BrandLogo } from '../components/BrandLogo';
import {
  CountUp,
  RubberSegment,
  Dock,
  type DockItemData,
  SpecularButton,
  TiltedCard,
  AnimatedList,
  type AnimatedListEntry,
  BorderGlow,
  AnimatedContent,
} from '../components/react-bits/index.js';
import { serverOrigin } from '../lib/server-origin';
import { applyTheme, readTheme, setTheme as persistTheme, type Theme } from '../lib/theme';
import {
  Button,
  Input,
  Textarea,
  Label,
  Checkbox,
  RadioGroup,
  RadioGroupItem,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  Separator,
} from '../components/ui';
import styles from './Landing.module.css';

interface Feature {
  icon: LucideIcon;
  title: string;
  note: string;
}

const CONTACT_URL = 'https://prakashinfotech.com/contact-us';
const GITHUB_URL = 'https://github.com/prakashsoftware/openline';

function GithubMark({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M12 .5C5.37.5 0 5.87 0 12.5c0 5.3 3.44 9.8 8.21 11.39.6.11.82-.26.82-.58 0-.29-.01-1.24-.02-2.25-3.34.73-4.04-1.42-4.04-1.42-.55-1.38-1.34-1.75-1.75-1.75-1.09-.75.08-.73.08-.73 1.2.08 1.84 1.24 1.84 1.24 1.07 1.83 2.81 1.3 3.5.99.11-.78.42-1.3.76-1.6-2.67-.3-5.47-1.34-5.47-5.95 0-1.31.47-2.39 1.24-3.23-.12-.3-.54-1.52.12-3.17 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 016 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.65.24 2.87.12 3.17.77.84 1.24 1.92 1.24 3.23 0 4.62-2.81 5.64-5.49 5.94.43.37.81 1.1.81 2.22 0 1.6-.01 2.9-.01 3.29 0 .32.22.7.83.58A12 12 0 0024 12.5C24 5.87 18.63.5 12 .5z" />
    </svg>
  );
}

const VISITOR_FEATURES: Feature[] = [
  { icon: Zap, title: 'Instant private answers.', note: 'Private LLM responds from the first keystroke — zero wait time for a human agent.' },
  { icon: UserCheck, title: 'Seamless agent takeover.', note: 'An agent claims the thread with one click. No handoff banner, no re-explaining.' },
  { icon: Video, title: 'In-page voice and video.', note: 'Direct P2P audio and video calls inside the widget. Zero downloads or link relays.' },
  { icon: MonitorPlay, title: 'Bi-directional screenshare.', note: 'High-resolution screenshare streams side-by-side with live camera tiles.' },
  { icon: History, title: 'Persistent visitor context.', note: 'Returning visitors are remembered automatically; chat threads persist across refreshes.' },
  { icon: Paperclip, title: 'Air-gapped file transfers.', note: 'Drag-and-drop documents, logs, and screenshots stored strictly on your local servers.' },
];

const AGENT_FEATURES: Feature[] = [
  { icon: Users, title: 'Live presence & visitor rail.', note: 'See real-time visitors, current page paths, duration, and geo-location across every site.' },
  { icon: Hand, title: 'One-click claiming with soft-locks.', note: 'Distributed soft-locks guarantee two agents never claim the same visitor simultaneously.' },
  { icon: Inbox, title: 'Unified multi-conversation rail.', note: 'Handle multiple simultaneous chats with unread markers, typing pings, and priority cues.' },
  { icon: Sparkles, title: 'Self-hosted AI summaries.', note: 'Synthesize full chat history, agent notes, and call transcripts using your local model.' },
  { icon: Video, title: 'Integrated WebRTC studio.', note: 'Dynamic multi-tile layouts, local session recording, and live mic/camera toggles.' },
  { icon: Route, title: 'Full session journey timeline.', note: 'Inspect the complete path of viewed pages and referrer sources for full support context.' },
];

const ADMIN_FEATURES: Feature[] = [
  { icon: Network, title: 'Multi-site tenancy in one console.', note: 'Configure multiple domains, embed tokens, and agent permissions from a unified portal.' },
  { icon: Palette, title: 'Pixel-perfect white-labeling.', note: 'Customize brand colors, Google fonts, widget dimensions, avatars, and tone of voice.' },
  { icon: Cpu, title: 'Bring-Your-Own-LLM (BYO-AI).', note: 'Run private Ollama, vLLM, or self-hosted OpenAI-compatible endpoints with zero leaks.' },
  { icon: Webhook, title: 'Enterprise webhook dispatch.', note: 'Trigger automated payloads to internal CRMs, ticketing queues, and event streams.' },
  { icon: MessageSquare, title: 'Canned slash commands.', note: 'Standardize quick responses across support teams with instant “/” keyboard shortcuts.' },
  { icon: SlidersHorizontal, title: 'Conversational pre-chat capture.', note: 'Capture qualified visitor leads with flexible conversational or form-based intake.' },
];

const CUSTOMIZATION_CARDS = [
  { icon: Palette, title: 'Brand & Palette Tokens', note: 'Define custom accent, paper, border, and text tokens for seamless brand alignment.', tag: 'Theme Engine' },
  { icon: TypeIcon, title: 'Custom Typography', note: 'Pair your brand’s Google Fonts or enterprise corporate webfonts with fluid typography clamp scales.', tag: 'Typography' },
  { icon: MousePointerClick, title: 'Custom Launcher & Avatars', note: 'Deploy floating pill buttons, animated badge triggers, or looping video agent avatars.', tag: 'Launcher Studio' },
  { icon: Cpu, title: 'Private AI & Model Routing', note: 'Point to internal Ollama, vLLM, or proprietary enterprise LLMs with zero third-party leakage.', tag: 'Model Gateway' },
];

const DEPLOY_ANIMATED_ITEMS: AnimatedListEntry[] = [
  {
    id: 'origin',
    icon: <Server size={18} />,
    title: 'One unified backend origin',
    description: 'A single lightweight Node container serves the real-time bus, API, agent app, and widget bundle.',
    meta: 'Node 20+',
  },
  {
    id: 'proxy',
    icon: <Lock size={18} />,
    title: 'Runs behind your reverse proxy',
    description: 'Seamlessly integrates with Nginx, Traefik, Caddy, or Kubernetes ingress with native TLS termination.',
    meta: 'TLS / Ingress',
  },
  {
    id: 'embed',
    icon: <Globe size={18} />,
    title: 'One-line script embed',
    description: 'Embed anywhere with a single script tag. Single-page applications (SPAs) are tracked automatically.',
    meta: '<script />',
  },
];

const SOVEREIGN_NODES = [
  {
    title: 'Direct WebSocket Bus (<12ms)',
    detail: 'Encrypted low-latency socket directly between visitor and your Node origin. Zero intermediaries.',
  },
  {
    title: 'Local Ollama / vLLM Gateway',
    detail: 'AI inferences execute strictly on your own GPU nodes. Zero prompts or transcripts leave your network.',
  },
  {
    title: 'Self-Hosted WebRTC Engine',
    detail: 'Direct browser-to-browser media stream with local audio/video file storage behind your reverse proxy.',
  },
  {
    title: 'Your Infrastructure & Encryption Keys',
    detail: 'Runs behind Nginx, Traefik, or Caddy. Full air-gapped readiness for HIPAA, SOC2, and GDPR compliance.',
  },
];

const TRADITIONAL_HOPS = [
  {
    title: 'Third-Party AI Cloud (US-East)',
    detail: 'Every visitor prompt, customer query, and support summary is sent to external cloud AI APIs.',
  },
  {
    title: 'Public Cloud Media Gateway',
    detail: 'Video and audio calls are routed and transcoded across shared multi-tenant SaaS relays.',
  },
  {
    title: 'SaaS Tracking & Telemetry',
    detail: 'Visitor IP addresses, session paths, and user metadata broadcast to public data aggregators.',
  },
  {
    title: 'Shared Multi-Tenant Storage',
    detail: 'Customer chat records stored in vendor-managed databases subject to subpoena and shared cloud leaks.',
  },
];

const TERMINAL_COMMANDS = {
  docker: `cat << 'EOF' > docker-compose.yml
services:
  openline:
    image: prakashsoftware/openline:latest
    container_name: openline_core
    restart: unless-stopped
    ports:
      - "3000:3000"
    environment:
      - PORT=3000
      - DATABASE_URL=sqlite:///data/openline.db
      - AI_PROVIDER=ollama
      - AI_ENDPOINT=http://ollama:11434
      - JWT_SECRET=change-this-production-key-secret
    volumes:
      - ./data:/data
EOF

# Start your sovereign support stack in seconds:
docker compose up -d`,
  helm: `# Add Openline private repository
helm repo add openline https://charts.openline.com
helm repo update

# Install with zero third-party cloud hops:
helm install openline openline/openline \\
  --namespace openline --create-namespace \\
  --set ingress.enabled=true \\
  --set ingress.host=support.yourcompany.internal \\
  --set ai.provider=local-vllm`,
  baremetal: `# Clone the MIT repository
git clone https://github.com/prakashsoftware/openline.git
cd openline

# Install dependencies and build bundles
pnpm install
pnpm build

# Launch the sovereign server
pnpm start`,
};

const FAQ_ITEMS = [
  {
    q: 'What does "zero third parties" mean in practice?',
    a: 'It means that every message, AI inference, audio packet, WebRTC video stream, and conversation recording stays strictly inside your infrastructure. No external SaaS vendor has access to the conversation payload, customer IP, or internal support transcripts.',
  },
  {
    q: 'Can we run private, air-gapped AI models like Ollama or vLLM?',
    a: 'Yes. Openline includes a native OpenAI-compatible gateway that hooks directly to locally hosted models (e.g., Llama 3, Mistral, or custom fine-tunes) running on your internal GPU nodes. Your customer data never leaves your VPC for AI inference.',
  },
  {
    q: 'How does deployment work in enterprise and regulated environments?',
    a: 'Openline runs as a single lightweight Docker container or Kubernetes pod behind your existing reverse proxy (Nginx, Traefik, Caddy, Cloudflare tunnel). It supports embedded SQLite for single-node deployments or PostgreSQL for high-availability clusters.',
  },
  {
    q: 'Is Openline suitable for HIPAA, GDPR, and SOC2 compliance?',
    a: 'Yes. Because zero data is transmitted to or stored by third parties, you retain 100% control over conversation retention, encryption keys, access logs, and audit trails.',
  },
  {
    q: 'Can we completely white-label the widget and agent console?',
    a: 'Completely. Your brand colors, typography, domain name, custom logo, launcher styles, and tone of voice. End-users and visitors will never see an external vendor watermark or link.',
  },
  {
    q: 'How easily can we integrate Openline into our existing web apps?',
    a: 'Integration takes a single script tag and a site token. Openline automatically tracks single-page application (SPA) route changes and requires zero invasive modifications to your host codebase.',
  },
];

const LANDING_IMG_VERSION = 'v20261026_human';

function Shot({
  ratio,
  label,
  src,
  alt,
  priority = true,
}: {
  ratio: '16x9' | '9x16' | '16x10' | 'widget';
  label: string;
  src?: string;
  alt?: string;
  priority?: boolean;
}) {
  const [loadError, setLoadError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);

  useEffect(() => {
    setLoadError(false);
    setCurrentSrc(src);
  }, [src]);

  const handleImageError = () => {
    // If versioned URL or failed, try bare path before fallback
    if (src && currentSrc !== src) {
      setCurrentSrc(src);
    } else {
      setLoadError(true);
    }
  };

  return (
    <div className={styles.shot} data-ratio={ratio}>
      {currentSrc && !loadError ? (
        <img
          className={styles.shotImg}
          src={currentSrc}
          alt={alt ?? ''}
          loading={priority ? 'eager' : 'lazy'}
          decoding="auto"
          onError={handleImageError}
        />
      ) : (
        <div className={styles.shotPlaceholder}>
          <span className={styles.shotLabel}>{label}</span>
          <span className={styles.shotRatio}>
            {ratio === '16x9' ? '16 : 9' : ratio === '16x10' ? '16 : 10' : ratio === 'widget' ? '960 : 1556' : '9 : 16'}
          </span>
        </div>
      )}
    </div>
  );
}

export function Landing() {
  const isAuthed = useAgentStore((s) => !!s.current);
  const [theme, setThemeState] = useState<Theme>('dark');
  const [scrolled, setScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [activePerspective, setActivePerspective] = useState<'visitors' | 'agents' | 'admins'>('visitors');
  const [activeTerminalTab, setActiveTerminalTab] = useState<'docker' | 'helm' | 'baremetal'>('docker');
  const [copied, setCopied] = useState(false);

  // shadcn Architecture Configurator State
  const [deployTarget, setDeployTarget] = useState<'onprem' | 'vpc' | 'airgap'>('vpc');
  const [cfgModules, setCfgModules] = useState({
    privateLlm: true,
    webrtcVideo: true,
    hipaaVault: true,
    screenshare: true,
    whiteLabel: true,
    webhookRelay: false,
  });
  const [cfgEmail, setCfgEmail] = useState('');
  const [cfgDomain, setCfgDomain] = useState('support.enterprise.corp');
  const [cfgNotes, setCfgNotes] = useState('');
  const [activeManifestTab, setActiveManifestTab] = useState<'compose' | 'k8s' | 'env'>('compose');
  const [specCopied, setSpecCopied] = useState(false);

  useEffect(() => {
    const t = readTheme();
    setThemeState(t);
    applyTheme(t);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Preload perspective tab images for instant tab switching with zero latency
  useEffect(() => {
    const imagesToPreload = [
      '/landing/widget.png',
      '/landing/Openline-agent-01.png',
      '/landing/Openline-chats.png',
      '/landing/visitor-video-call-notification.png',
      '/landing/visitor-video-view.png',
      '/landing/agent-video-view.png',
      '/landing/agent-screenshare-view.png',
    ];
    imagesToPreload.forEach((path) => {
      const img = new Image();
      img.src = path;
    });
  }, []);

  // Embed the live Openline widget on the landing page for immediate dogfooding
  useEffect(() => {
    const token = (import.meta.env.VITE_SITE_TOKEN as string | undefined) || 'st_demo_openline_v1';
    const script = document.createElement('script');
    script.src = `${serverOrigin()}/openline.js`;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-site-token', token);
    script.setAttribute('data-openline-landing', '');
    document.body.appendChild(script);
    return () => {
      try {
        (window as unknown as { Openline?: { close?: () => void } }).Openline?.close?.();
      } catch {
        /* widget not mounted */
      }
      script.remove();
    };
  }, []);

  function toggleTheme() {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setThemeState(next);
    persistTheme(next);
  }

  function scrollToSection(id: string) {
    return (e?: React.MouseEvent) => {
      e?.preventDefault();
      setMobileNavOpen(false);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    };
  }

  function startChat() {
    const w = window as unknown as { Openline?: { open?: () => void } };
    if (w.Openline?.open) {
      w.Openline.open();
      return;
    }
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  }

  function handleCopyCommand(text: string) {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const activeModuleCount = Object.values(cfgModules).filter(Boolean).length;

  function generateManifestText(): string {
    const domain = cfgDomain.trim() || 'support.enterprise.corp';
    if (activeManifestTab === 'compose') {
      return `# Openline Sovereign Architecture Deployment Spec
# Target Topology: ${deployTarget.toUpperCase()}
# Generated for: ${cfgEmail || 'enterprise-admin@internal'}
# Active Sovereign Modules: ${activeModuleCount} of 6

version: '3.8'
services:
  openline-core:
    image: ghcr.io/prakashsoftware/openline:latest
    restart: unless-stopped
    ports:
      - "3000:3000"
    environment:
      - NODE_ENV=production
      - OPENLINE_DOMAIN=${domain}
      - DATA_SOVEREIGNTY=true
      - TELEMETRY_DISABLED=true${cfgModules.privateLlm ? '\n      - AI_PROVIDER=ollama\n      - OLLAMA_HOST=http://ollama-service:11434\n      - OLLAMA_MODEL=mistral:7b-instruct' : ''}${cfgModules.webrtcVideo ? '\n      - WEBRTC_ENABLED=true\n      - STUN_SERVER=stun:turn.internal.corp:3478' : ''}${cfgModules.hipaaVault ? '\n      - HIPAA_AUDIT_LOGS=enabled\n      - ENCRYPTION_AT_REST=AES-256-GCM\n      - TAMPER_EVIDENT_HASHING=sha256' : ''}${cfgModules.screenshare ? '\n      - SCREENSHARE_P2P=enabled' : ''}${cfgModules.whiteLabel ? '\n      - WHITELABEL_PERMITTED=true\n      - CUSTOM_THEME_ENGINE=active' : ''}${cfgModules.webhookRelay ? '\n      - WEBHOOK_DISPATCHER=internal-bus' : ''}
    volumes:
      - /var/openline/data:/data/sqlite
      - /var/openline/logs:/data/audit-logs
    networks:
      - sovereign-lan

networks:
  sovereign-lan:
    driver: bridge
    internal: ${deployTarget === 'airgap' ? 'true # STRICT AIR-GAP: Zero external gateway' : 'false'}`;
    }

    if (activeManifestTab === 'k8s') {
      return `# Openline Kubernetes Sovereign Deployment
apiVersion: apps/v1
kind: Deployment
metadata:
  name: openline-sovereign-bus
  namespace: sovereign-apps
spec:
  replicas: 2
  selector:
    matchLabels:
      app: openline
  template:
    metadata:
      labels:
        app: openline
    spec:
      containers:
        - name: openline
          image: ghcr.io/prakashsoftware/openline:latest
          ports:
            - containerPort: 3000
          env:
            - name: OPENLINE_DOMAIN
              value: "${domain}"
            - name: AIRGAP_MODE
              value: "${deployTarget === 'airgap' ? 'true' : 'false'}"
            - name: HIPAA_MODE
              value: "${cfgModules.hipaaVault ? 'strict' : 'standard'}"`;
    }

    return `# Openline Sovereign Environment Configuration (.env)
# Host origin
OPENLINE_DOMAIN=${domain}
DEPLOYMENT_TARGET=${deployTarget}

# Privacy & Security Switches
DATA_SOVEREIGNTY=true
OUTBOUND_TELEMETRY=false
AIRGAP_ISOLATION=${deployTarget === 'airgap'}

# Enabled Capability Modules
MODULE_PRIVATE_LLM=${cfgModules.privateLlm}
MODULE_WEBRTC_CALLS=${cfgModules.webrtcVideo}
MODULE_HIPAA_VAULT=${cfgModules.hipaaVault}
MODULE_SCREENSHARE=${cfgModules.screenshare}
MODULE_WHITELABEL=${cfgModules.whiteLabel}
MODULE_WEBHOOK_RELAY=${cfgModules.webhookRelay}

# Contact/Owner Spec
CONFIG_OWNER=${cfgEmail || 'admin@' + domain}
${cfgNotes ? '# Architecture Notes:\n# ' + cfgNotes.replace(/\n/g, '\n# ') : ''}`;
  }

  function handleCopyManifest() {
    navigator.clipboard.writeText(generateManifestText());
    setSpecCopied(true);
    setTimeout(() => setSpecCopied(false), 2000);
  }

  const signInTo = isAuthed ? '/dashboard' : '/login';
  const signInLabel = isAuthed ? 'Open dashboard' : 'Agent sign-in';

  const currentFeatures =
    activePerspective === 'visitors'
      ? VISITOR_FEATURES
      : activePerspective === 'agents'
        ? AGENT_FEATURES
        : ADMIN_FEATURES;

  const dockItems: DockItemData[] = [
    {
      id: 'arch',
      icon: <ShieldCheck size={19} />,
      label: 'Architecture',
      href: '#architecture',
      onClick: scrollToSection('architecture'),
    },
    {
      id: 'capabilities',
      icon: <Sparkles size={19} />,
      label: 'Capabilities',
      href: '#capabilities',
      onClick: scrollToSection('capabilities'),
    },
    {
      id: 'experience',
      icon: <Zap size={19} />,
      label: 'Experience',
      href: '#experience',
      onClick: scrollToSection('experience'),
    },
    {
      id: 'video',
      icon: <Video size={19} />,
      label: 'Voice & Video',
      href: '#video',
      onClick: scrollToSection('video'),
    },
    {
      id: 'customize',
      icon: <Palette size={19} />,
      label: 'White-Label',
      href: '#customize',
      onClick: scrollToSection('customize'),
    },
    {
      id: 'deploy',
      icon: <Terminal size={19} />,
      label: 'DevOps Deploy',
      href: '#deploy',
      onClick: scrollToSection('deploy'),
    },
    {
      id: 'configurator',
      icon: <SlidersHorizontal size={19} />,
      label: 'Configurator',
      href: '#configurator',
      onClick: scrollToSection('configurator'),
    },
    {
      id: 'faq',
      icon: <MessageSquare size={19} />,
      label: 'FAQ',
      href: '#faq',
      onClick: scrollToSection('faq'),
    },
    {
      id: 'chat',
      icon: <MessageSquareText size={19} />,
      label: 'Start Live Chat',
      onClick: startChat,
      badge: 'Live',
    },
    {
      id: 'theme',
      icon: theme === 'dark' ? <Sun size={19} /> : <Moon size={19} />,
      label: theme === 'dark' ? 'Light Mode' : 'Dark Mode',
      onClick: toggleTheme,
    },
  ];

  return (
    <div className={styles.page}>
      {/* ── Top Navigation Bar ───────────────────────────────── */}
      <header className={styles.nav} data-scrolled={scrolled ? 'true' : 'false'}>
        <div className={styles.navInner}>
          <a href="#top" onClick={scrollToSection('top')} className={styles.brandLink} aria-label="Openline home">
            <BrandLogo variant="wordmark" height={26} />
          </a>

          <nav className={styles.navLinks} aria-label="Primary navigation">
            <a className={styles.navLink} href="#architecture" onClick={scrollToSection('architecture')}>
              Architecture
            </a>
            <a className={styles.navLink} href="#experience" onClick={scrollToSection('experience')}>
              Experience
            </a>
            <a className={styles.navLink} href="#video" onClick={scrollToSection('video')}>
              Voice &amp; Video
            </a>
            <a className={styles.navLink} href="#customize" onClick={scrollToSection('customize')}>
              Customization
            </a>
            <a className={styles.navLink} href="#deploy" onClick={scrollToSection('deploy')}>
              Deploy
            </a>
            <a className={styles.navLink} href="#configurator" onClick={scrollToSection('configurator')}>
              Configurator
            </a>
            <a className={styles.navLink} href="#faq" onClick={scrollToSection('faq')}>
              FAQ
            </a>
          </nav>


          <div className={styles.navActions}>
            <Button
              variant="ghost"
              size="icon"
              className={styles.themeToggle}
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
            </Button>

            <Button variant="ghost" size="sm" asChild className={styles.signIn}>
              <Link to={signInTo}>
                {signInLabel}
              </Link>
            </Button>

            {!isAuthed && (
              <Button variant="ghost" size="sm" asChild className={styles.signIn}>
                <Link to="/signup">
                  Sign up
                </Link>
              </Button>
            )}

            <Button variant="default" size="sm" className={styles.navCtaBtn} onClick={startChat}>
              <span>Start live chat</span>
              <ArrowRight size={15} aria-hidden="true" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className={styles.menuToggle}
              onClick={() => setMobileNavOpen((o) => !o)}
              aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileNavOpen}
            >
              {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
          </div>
        </div>
      </header>

      {/* ── Mobile Navigation Drawer ────────────────────────── */}
      {mobileNavOpen && (
        <div className={styles.mobileDrawer} role="dialog" aria-modal="true" aria-label="Mobile Navigation">
          <div className={styles.mobileDrawerHeader}>
            <BrandLogo variant="wordmark" height={24} />
            <Button
              variant="ghost"
              size="icon"
              className={styles.mobileDrawerClose}
              onClick={() => setMobileNavOpen(false)}
              aria-label="Close menu"
            >
              <X size={20} />
            </Button>
          </div>
          <div className={styles.mobileDrawerLinks}>
            <a className={styles.mobileDrawerLink} href="#architecture" onClick={scrollToSection('architecture')}>
              <span>Architecture</span>
              <ChevronRight size={18} />
            </a>
            <a className={styles.mobileDrawerLink} href="#experience" onClick={scrollToSection('experience')}>
              <span>Platform Experience</span>
              <ChevronRight size={18} />
            </a>
            <a className={styles.mobileDrawerLink} href="#video" onClick={scrollToSection('video')}>
              <span>Voice &amp; Video WebRTC</span>
              <ChevronRight size={18} />
            </a>
            <a className={styles.mobileDrawerLink} href="#customize" onClick={scrollToSection('customize')}>
              <span>White-label Customization</span>
              <ChevronRight size={18} />
            </a>
            <a className={styles.mobileDrawerLink} href="#deploy" onClick={scrollToSection('deploy')}>
              <span>DevOps 60-Second Deploy</span>
              <ChevronRight size={18} />
            </a>
            <a className={styles.mobileDrawerLink} href="#configurator" onClick={scrollToSection('configurator')}>
              <span>Sovereign Configurator</span>
              <ChevronRight size={18} />
            </a>
            <a className={styles.mobileDrawerLink} href="#faq" onClick={scrollToSection('faq')}>
              <span>FAQ</span>
              <ChevronRight size={18} />
            </a>
          </div>
          <div className={styles.mobileDrawerActions}>
            <div className={styles.mobileDrawerMeta}>
              <Button
                variant="ghost"
                size="icon"
                className={styles.themeToggle}
                onClick={toggleTheme}
                aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </Button>
              <Button variant="ghost" size="sm" asChild className={styles.signIn}>
                <Link to={signInTo} onClick={() => setMobileNavOpen(false)}>
                  {signInLabel}
                </Link>
              </Button>
              {!isAuthed && (
                <Button variant="ghost" size="sm" asChild className={styles.signIn}>
                  <Link to="/signup" onClick={() => setMobileNavOpen(false)}>
                    Sign up
                  </Link>
                </Button>
              )}
              <a className={styles.githubLink} href={GITHUB_URL} target="_blank" rel="noreferrer">
                <GithubMark size={16} /> <span>GitHub</span>
              </a>
            </div>
            <Button
              variant="default"
              size="lg"
              className={styles.primaryBtn}
              onClick={() => {
                setMobileNavOpen(false);
                startChat();
              }}
            >
              Start live chat <ArrowRight size={16} aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}

      {/* ── Hero Section ────────────────────────────────────── */}
      <section className={styles.hero} id="top">
        <div className={styles.heroAura} aria-hidden="true" />
        <div className={styles.heroGridLines} aria-hidden="true" />
        <div className={styles.container}>
          <AnimatedContent trigger="mount" distance={30} duration={0.8} ease="power3.out">
            <div className={styles.heroContent}>

              <h1 className={styles.heroTitle}>
                The sovereign alternative to <br className={styles.heroBreak} />
                <span className={styles.heroAccent}>Intercom &amp; Zendesk.</span>
              </h1>

              <p className={styles.heroLede}>
                Openline is self-hosted live chat, private AI agents, and WebRTC video support that lives strictly on
                your own infrastructure. Zero third parties in the conversation path. Engineered for regulated healthcare,
                finance, and enterprise data sovereignty.
              </p>

              <div className={styles.heroActions}>
                <SpecularButton
                  size="md"
                  radius={9999}
                  tint="#0979f5"
                  tintOpacity={0.92}
                  textColor="#ffffff"
                  lineColor="#ffffff"
                  baseColor="#0660c7"
                  intensity={1.25}
                  idleIntensity={0.4}
                  glow={1.2}
                  onClick={startChat}
                  className={styles.heroSpecularBtn}
                >
                  <span>Start live chat</span>
                  <ArrowRight size={16} aria-hidden="true" />
                </SpecularButton>
                <Button variant="outline" size="lg" asChild className={styles.secondaryBtn}>
                  <a href="#configurator" onClick={scrollToSection('configurator')}>
                    <span>Configure cluster</span>
                    <ArrowUpRight size={16} aria-hidden="true" />
                  </a>
                </Button>
              </div>

              <div className={styles.heroTrustStrip}>
                <div className={styles.trustItem}>
                  <ShieldCheck size={14} className={styles.trustIcon} />
                  <span>100% Open Source (MIT)</span>
                </div>
                <span className={styles.trustDot}>·</span>
                <div className={styles.trustItem}>
                  <Lock size={14} className={styles.trustIcon} />
                  <span>Air-Gapped Ready</span>
                </div>
                <span className={styles.trustDot}>·</span>
                <div className={styles.trustItem}>
                  <Database size={14} className={styles.trustIcon} />
                  <span>No Data Leaves Your VPC</span>
                </div>
                <span className={styles.trustDot}>·</span>
                <div className={styles.trustItem}>
                  <Terminal size={14} className={styles.trustIcon} />
                  <span>Docker &amp; K8s Ready</span>
                </div>
              </div>
            </div>
          </AnimatedContent>

          {/* ── Cockpit Showcase Frame ──────────────────────── */}
          <AnimatedContent trigger="scroll" distance={40} duration={0.85} ease="power3.out" delay={0.1}>
            <div className={styles.cockpitStudio}>
              <div className={styles.cockpitHardwareFrame}>
                <div className={styles.cockpitTitlebar}>
                  <div className={styles.cockpitWindowControls}>
                  <span className={`${styles.windowDot} ${styles.dotClose}`} />
                  <span className={`${styles.windowDot} ${styles.dotMinimize}`} />
                  <span className={`${styles.windowDot} ${styles.dotExpand}`} />
                </div>
                <div className={styles.cockpitUrlBar}>
                  <Lock size={11} className={styles.cockpitLockIcon} />
                  <span>openline.internal · direct websocket bus</span>
                </div>
                <div className={styles.cockpitTelemetryBadge}>
                  <span className={styles.livePulseDot} />
                  <span>BUS LATENCY &lt;12ms · 0 HOPS</span>
                </div>
              </div>

              <div className={styles.cockpitScreenContainer}>
                <Shot
                  priority
                  ratio="16x9"
                  label="Openline Sovereign Agent Cockpit"
                  src="/landing/Openline-live.png"
                  alt="Openline sovereign live agent monitor showcasing real-time visitor activity and zero-leak telemetry"
                />
              </div>
            </div>

            {/* Contextual Architectural Annotations */}
            <div className={styles.cockpitCalloutGrid}>
              <BorderGlow
                glowColor="#0979f5"
                borderRadius={16}
                backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
                className={styles.borderGlowCardWrap}
              >
                <div className={styles.calloutCard}>
                  <div className={styles.calloutHeader}>
                    <Shield size={16} className={styles.calloutIcon} />
                    <h4>Zero Outbound Telemetry</h4>
                  </div>
                  <p>No Segment, Datadog, or external analytics beacons. Visitor sessions and IP data remain strictly on-premise.</p>
                </div>
              </BorderGlow>
              <BorderGlow
                glowColor="#0979f5"
                borderRadius={16}
                backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
                className={styles.borderGlowCardWrap}
              >
                <div className={styles.calloutCard}>
                  <div className={styles.calloutHeader}>
                    <Zap size={16} className={styles.calloutIcon} />
                    <h4>Instant P2P WebRTC Hand-Off</h4>
                  </div>
                  <p>Transition visitors seamlessly from private AI responses to live human agents and 4K screensharing in one tap.</p>
                </div>
              </BorderGlow>
            </div>
          </div>
        </AnimatedContent>
      </div>
    </section>

      {/* ── Data Sovereignty & Wire-Level Containment (Image 2 Redesign) ─ */}
      <section className={styles.proofSection} id="sovereignty">
        <div className={styles.container}>
          <div className={styles.proofEditorialSplit}>
            {/* Left Column: Pill + Title with Highlight + Description + Guarantee Badge + Wave Ribbon */}
            <AnimatedContent trigger="scroll" distance={30} duration={0.8} ease="power3.out">
              <div className={styles.proofEditorialLead}>
                <div className={styles.sovereigntyPill}>
                <Shield size={14} className={styles.sovereigntyPillIcon} />
                <span>DATA SOVEREIGNTY</span>
              </div>
              <h2 className={styles.proofLeadTitle}>
                Customer conversations are too sensitive for{' '}
                <span className={styles.cloudSaaSHighlight}>shared cloud SaaS.</span>
              </h2>
              <p className={styles.proofLeadText}>
                Regulated institutions in healthcare, banking, and government cannot broadcast sensitive customer
                transcripts, voice recordings, and AI prompts across multi-tenant US cloud aggregators. Openline
                guarantees complete wire-level containment.
              </p>

              {/* Contained Guarantee Banner */}
              <div className={styles.containedGuarantee}>
                <div className={styles.containedGuaranteeIcon}>
                  <ShieldCheck size={18} />
                </div>
                <div className={styles.containedGuaranteeDivider} />
                <div className={styles.containedGuaranteeContent}>
                  <div className={styles.containedGuaranteeTagline}>YOUR DATA. YOUR INFRASTRUCTURE.</div>
                  <div className={styles.containedGuaranteeSub}>100% CONTAINED · 100% COMPLIANT</div>
                </div>
              </div>

              {/* Decorative Animated Wave Ribbon */}
              <div className={styles.proofWaveWrapper} aria-hidden="true">
                <svg
                  className={styles.proofWaveRibbon}
                  viewBox="0 0 540 120"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient id="waveGradCyan" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.85" />
                      <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.65" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0.2" />
                    </linearGradient>
                    <linearGradient id="waveGradBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.7" />
                      <stop offset="60%" stopColor="#06b6d4" stopOpacity="0.5" />
                      <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1" />
                    </linearGradient>
                    <linearGradient id="waveGradGreen" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.6" />
                      <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 0 95 C 90 95, 140 35, 230 45 C 320 55, 380 115, 540 65"
                    stroke="url(#waveGradCyan)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    className={styles.wavePath1}
                  />
                  <path
                    d="M 0 102 C 100 102, 160 45, 250 55 C 340 65, 390 110, 540 75"
                    stroke="url(#waveGradBlue)"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    className={styles.wavePath2}
                  />
                  <path
                    d="M 0 110 C 110 110, 180 55, 270 65 C 360 75, 410 105, 540 85"
                    stroke="url(#waveGradGreen)"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    className={styles.wavePath3}
                  />
                  <path
                    d="M 0 88 C 80 88, 130 25, 220 35 C 310 45, 370 120, 540 55"
                    stroke="url(#waveGradCyan)"
                    strokeWidth="1.2"
                    strokeDasharray="4 6"
                    className={styles.wavePath4}
                  />
                </svg>
              </div>
            </div>
          </AnimatedContent>

          {/* Right Column: 5-Card Asymmetric Bento Grid matching Image 2 */}
          <AnimatedContent trigger="scroll" distance={35} duration={0.85} delay={0.1} ease="power3.out">
            <div className={styles.sovereigntyCardsGrid}>
              {/* Top Row: 3 Cards */}
              <div className={styles.sovereigntyTopRow}>
                {/* Card 1: 0 Third-Party Cloud Hops */}
                <BorderGlow
                  glowColor="#38bdf8"
                  borderRadius={18}
                  backgroundColor={theme === 'dark' ? 'rgba(11, 15, 25, 0.95)' : 'rgba(255, 255, 255, 0.98)'}
                  className={styles.sovereigntyBorderCard}
                >
                  <div className={`${styles.sovereigntyCard} ${styles.cardGlowBlue}`}>
                    <div className={styles.sovereigntyCardTop}>
                      <div className={`${styles.cardIconFrame} ${styles.cardIconBlue}`}>
                        <Share2 size={16} />
                      </div>
                    </div>
                    <div className={styles.sovereigntyMetricNum}>
                      <CountUp from={10} to={0} duration={1.2} />
                    </div>
                    <div className={styles.sovereigntyMetricTitle}>Third-Party Cloud Hops</div>
                    <div className={styles.sovereigntyMetricDesc}>
                      All messages, audio streams, and media stay within your VPC perimeter.
                    </div>
                    <div className={styles.sovereigntyCardBottom}>
                      <span className={styles.cardPillBlue}>ZERO LEAKS</span>
                    </div>
                  </div>
                </BorderGlow>

                {/* Card 2: 100% Data Sovereignty */}
                <BorderGlow
                  glowColor="#10b981"
                  borderRadius={18}
                  backgroundColor={theme === 'dark' ? 'rgba(11, 15, 25, 0.95)' : 'rgba(255, 255, 255, 0.98)'}
                  className={styles.sovereigntyBorderCard}
                >
                  <div className={`${styles.sovereigntyCard} ${styles.cardGlowGreen}`}>
                    <div className={styles.sovereigntyCardTop}>
                      <div className={`${styles.cardIconFrame} ${styles.cardIconGreen}`}>
                        <ShieldCheck size={16} />
                      </div>
                    </div>
                    <div className={styles.sovereigntyMetricNum}>
                      <CountUp from={0} to={100} duration={1.5} />%
                    </div>
                    <div className={styles.sovereigntyMetricTitle}>Data Sovereignty</div>
                    <div className={styles.sovereigntyMetricDesc}>
                      You control encryption keys, retention policies, and compliance audit logs.
                    </div>
                    <div className={styles.sovereigntyCardBottom}>
                      <span className={styles.cardPillGreen}>AIR-GAPPED</span>
                    </div>
                  </div>
                </BorderGlow>

                {/* Card 3: <12ms Real-Time WebSocket Latency */}
                <BorderGlow
                  glowColor="#a855f7"
                  borderRadius={18}
                  backgroundColor={theme === 'dark' ? 'rgba(11, 15, 25, 0.95)' : 'rgba(255, 255, 255, 0.98)'}
                  className={styles.sovereigntyBorderCard}
                >
                  <div className={`${styles.sovereigntyCard} ${styles.cardGlowPurple}`}>
                    <div className={styles.sovereigntyCardTop}>
                      <div className={`${styles.cardIconFrame} ${styles.cardIconPurple}`}>
                        <Clock size={16} />
                      </div>
                    </div>
                    <div className={styles.sovereigntyMetricNum}>
                      &lt;<CountUp from={60} to={12} duration={1.2} />ms
                    </div>
                    <div className={styles.sovereigntyMetricTitle}>Real-Time WebSocket Latency</div>
                    <div className={styles.sovereigntyMetricDesc}>
                      Low-latency bidirectional WebSocket connection directly to your Node origin.
                    </div>
                    <div className={styles.sovereigntyCardBottom}>
                      <span className={styles.cardPillPurple}>DIRECT BUS</span>
                    </div>
                  </div>
                </BorderGlow>
              </div>

              {/* Bottom Row: 2 Cards (Card 4: 99.99% Reliability + Card 5: 3D Cyber Shield Visual) */}
              <div className={styles.sovereigntyBottomRow}>
                {/* Card 4: 99.99% Operational Reliability */}
                <BorderGlow
                  glowColor="#2563eb"
                  borderRadius={18}
                  backgroundColor={theme === 'dark' ? 'rgba(11, 15, 25, 0.95)' : 'rgba(255, 255, 255, 0.98)'}
                  className={styles.sovereigntyBorderCard}
                >
                  <div className={`${styles.sovereigntyCard} ${styles.cardGlowDeepBlue}`}>
                    <div className={styles.sovereigntyCardTop}>
                      <div className={`${styles.cardIconFrame} ${styles.cardIconBlue}`}>
                        <Shield size={16} />
                      </div>
                    </div>
                    <div className={styles.sovereigntyMetricNum}>
                      <CountUp from={90} to={99.99} decimals={2} duration={1.5} />%
                    </div>
                    <div className={styles.sovereigntyMetricTitle}>Operational Reliability</div>
                    <div className={styles.sovereigntyMetricDesc}>
                      Zero vulnerability to third-party vendor cloud outages or API rate limit spikes.
                    </div>
                    <div className={styles.sovereigntyCardBottom}>
                      <span className={styles.cardPillBlue}>SELF-HOSTED</span>
                    </div>
                  </div>
                </BorderGlow>

                {/* Card 5: 3D Cyber Security Shield Showcase Card */}
                <BorderGlow
                  glowColor="#06b6d4"
                  borderRadius={18}
                  backgroundColor={theme === 'dark' ? 'rgba(10, 14, 24, 0.98)' : 'rgba(255, 255, 255, 0.98)'}
                  className={`${styles.sovereigntyBorderCard} ${styles.shieldShowcaseCardWrap}`}
                >
                  <div className={styles.shieldVisualCard}>
                    <div className={styles.shieldBackdropAura} />
                    {/* Floating 3D Shield Image + Dynamic Cyber Vector Overlay */}
                    <div className={styles.shield3dContainer}>
                      <img
                        src="/landing/cyber-shield-3d.jpg"
                        alt="Hardware-Grade Cryptographic Sovereignty 3D Shield"
                        className={styles.shield3dImage}
                        loading="lazy"
                      />
                      {/* Interactive floating cyber aura with orbital rings and particles */}
                      <div className={styles.shieldCyberOverlay}>
                        <div className={styles.orbitalRingPulse} />
                        <div className={styles.shieldStatusBadge}>
                          <span className={styles.shieldStatusDot} />
                          <span>E2EE ACTIVE</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </BorderGlow>
              </div>
            </div>
          </AnimatedContent>
        </div>
      </div>
    </section>

      {/* ── Architecture: Wire-Level Difference (Side-by-Side) ─ */}
      <section className={styles.architectureSection} id="architecture">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.kicker}>Network Security</span>
            <h2 className={styles.sectionTitle}>The wire-level difference.</h2>
            <p className={styles.sectionSubtitle}>
              Examine the concrete engineering difference between Openline’s sovereign direct bus and traditional SaaS cloud sprawl.
            </p>
          </div>

          <AnimatedContent trigger="scroll" distance={40} duration={0.85} ease="power3.out">
            <div className={styles.wireComparisonGrid}>
              {/* Openline Sovereign Stack */}
              <BorderGlow
                glowColor="#10b981"
                borderRadius={16}
                backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
                className={styles.borderGlowCardWrap}
              >
                <div className={styles.wireColumnSovereign}>
                  <div className={styles.wireColHeader}>
                    <div className={styles.wireColIconSovereign}>
                      <CheckCircle2 size={20} />
                    </div>
                    <div>
                      <h3 className={styles.wireColTitle}>Openline Sovereign Stack</h3>
                      <p className={styles.wireColSub}>Direct, private connection behind your firewall</p>
                    </div>
                  </div>

                  <div className={styles.wireNodeList}>
                    {SOVEREIGN_NODES.map((node) => (
                      <div key={node.title} className={styles.wireNodeItemSovereign}>
                        <div className={styles.wireNodeBulletSovereign}>
                          <Check size={14} />
                        </div>
                        <div>
                          <h4 className={styles.wireNodeItemTitle}>{node.title}</h4>
                          <p className={styles.wireNodeItemDetail}>{node.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </BorderGlow>

              {/* Traditional Cloud SaaS */}
              <BorderGlow
                glowColor="#ef4444"
                borderRadius={16}
                backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
                className={styles.borderGlowCardWrap}
              >
                <div className={styles.wireColumnTraditional}>
                  <div className={styles.wireColHeader}>
                    <div className={styles.wireColIconTraditional}>
                      <XCircle size={20} />
                    </div>
                    <div>
                      <h3 className={styles.wireColTitle}>Traditional Cloud SaaS</h3>
                      <p className={styles.wireColSub}>Multi-tenant public relay sprawl</p>
                    </div>
                  </div>

                  <div className={styles.wireNodeList}>
                    {TRADITIONAL_HOPS.map((node) => (
                      <div key={node.title} className={styles.wireNodeItemTraditional}>
                        <div className={styles.wireNodeBulletTraditional}>
                          <X size={14} />
                        </div>
                        <div>
                          <h4 className={styles.wireNodeItemTitle}>{node.title}</h4>
                          <p className={styles.wireNodeItemDetail}>{node.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </BorderGlow>
            </div>
          </AnimatedContent>
        </div>
      </section>

      {/* ── Platform Perspectives: Command Console ──────────── */}
      <section className={styles.section} id="experience">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.kicker}>Unified Ecosystem</span>
            <h2 className={styles.sectionTitle}>Engineered for everyone on the wire.</h2>
            <p className={styles.sectionSubtitle}>
              Three specialized perspectives designed for seamless collaboration: visitors, support agents, and compliance admins.
            </p>
          </div>

          <div className={styles.consoleTabWrap}>
            <RubberSegment
              items={[
                {
                  value: 'visitors',
                  label: 'For Visitors',
                  icon: <Zap size={15} strokeWidth={2.2} />,
                },
                {
                  value: 'agents',
                  label: 'For Agents',
                  icon: <Users size={15} strokeWidth={2.2} />,
                },
                {
                  value: 'admins',
                  label: 'For Admins & Compliance',
                  icon: <Lock size={15} strokeWidth={2.2} />,
                },
              ]}
              value={activePerspective}
              onChange={(val) => setActivePerspective(val as 'visitors' | 'agents' | 'admins')}
              trackColor={theme === 'dark' ? 'rgba(17, 19, 31, 0.95)' : '#ffffff'}
              thumbColor="#0979f5"
              textColor={theme === 'dark' ? 'rgba(255, 255, 255, 0.72)' : '#475569'}
              hoverTextColor={theme === 'dark' ? '#ffffff' : '#090d16'}
              activeTextColor="#ffffff"
              size="lg"
              radius={9999}
              inset={4}
              equalSlots={false}
              stretch={110}
              squash={4}
              speed={1}
              glide={75}
              draggable={true}
              aria-label="Platform Perspectives"
            />
          </div>

          <div className={styles.perspectiveShowcase}>
            <div className={styles.perspectiveManifest}>
              <div className={styles.manifestHeader}>
                <h3 className={styles.manifestHeading}>
                  {activePerspective === 'visitors' && 'Fast, respectful visitor experience.'}
                  {activePerspective === 'agents' && 'A command center built for speed.'}
                  {activePerspective === 'admins' && 'Total governance & security control.'}
                </h3>
                <p className={styles.manifestSub}>
                  {activePerspective === 'visitors' && 'In-page assistance without redirects, popups, or battery drain.'}
                  {activePerspective === 'agents' && 'Unified chat, call escalation, session timelines, and AI summaries in one view.'}
                  {activePerspective === 'admins' && 'Audit logs, custom LLM configurations, webhook relays, and site isolation.'}
                </p>
              </div>

              <div className={styles.manifestGrid}>
                {currentFeatures.map((f) => {
                  const Ic = f.icon;
                  return (
                    <TiltedCard
                      key={f.title}
                      containerWidth="100%"
                      containerHeight="100%"
                      imageWidth="100%"
                      imageHeight="100%"
                      scaleOnHover={1.03}
                      rotateAmplitude={9}
                      showTooltip={false}
                      glare={0.35}
                      glareColor="#0979f5"
                      radius={12}
                      className={styles.tiltedCardWrapper}
                    >
                      <BorderGlow
                        glowColor="#0979f5"
                        borderRadius={12}
                        backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
                        className={styles.borderGlowCardWrap}
                      >
                        <div className={styles.manifestCard}>
                          <div className={styles.manifestIconWrap}>
                            <Ic size={18} />
                          </div>
                          <div>
                            <h4 className={styles.manifestItemTitle}>{f.title}</h4>
                            <p className={styles.manifestItemNote}>{f.note}</p>
                          </div>
                        </div>
                      </BorderGlow>
                    </TiltedCard>
                  );
                })}
              </div>
            </div>

            <div className={styles.perspectiveVisualPane}>
              {activePerspective === 'visitors' && (
                <div className={styles.widgetMockupWrapper}>
                  <div className={styles.perspectiveWindowBar}>
                    <div className={styles.cockpitWindowControls}>
                      <span className={`${styles.windowDot} ${styles.dotClose}`} />
                      <span className={`${styles.windowDot} ${styles.dotMinimize}`} />
                      <span className={`${styles.windowDot} ${styles.dotExpand}`} />
                    </div>
                    <span className={styles.perspectiveWindowLabel}>
                      <Smartphone size={13} className={styles.accentText} /> Visitor Widget
                    </span>
                    <span className={styles.perspectiveWindowStatus}>
                      <span className={styles.livePulseDot} /> Online
                    </span>
                  </div>
                  <Shot
                    ratio="widget"
                    label="Visitor chat widget"
                    src="/landing/widget.png"
                    alt="Openline visitor chat widget with instant AI and real-time agent handoff"
                    priority
                  />
                </div>
              )}

              {activePerspective === 'agents' && (
                <div className={styles.perspectiveWindowWrapper}>
                  <div className={styles.perspectiveWindowBar}>
                    <div className={styles.cockpitWindowControls}>
                      <span className={`${styles.windowDot} ${styles.dotClose}`} />
                      <span className={`${styles.windowDot} ${styles.dotMinimize}`} />
                      <span className={`${styles.windowDot} ${styles.dotExpand}`} />
                    </div>
                    <span className={styles.perspectiveWindowLabel}>
                      <Users size={13} className={styles.accentText} /> Agent Conversation Workspace
                    </span>
                    <span className={styles.perspectiveWindowStatus}>
                      <span className={styles.livePulseDot} /> Real-time Hand-off
                    </span>
                  </div>
                  <Shot
                    ratio="16x9"
                    label="Agent conversation view"
                    src="/landing/Openline-agent-01.png"
                    alt="Openline agent conversation workspace with live visitor journey and WebRTC video"
                    priority
                  />
                </div>
              )}

              {activePerspective === 'admins' && (
                <div className={styles.perspectiveWindowWrapper}>
                  <div className={styles.perspectiveWindowBar}>
                    <div className={styles.cockpitWindowControls}>
                      <span className={`${styles.windowDot} ${styles.dotClose}`} />
                      <span className={`${styles.windowDot} ${styles.dotMinimize}`} />
                      <span className={`${styles.windowDot} ${styles.dotExpand}`} />
                    </div>
                    <span className={styles.perspectiveWindowLabel}>
                      <Lock size={13} className={styles.accentText} /> Compliance Audit &amp; Retention Log
                    </span>
                    <span className={styles.perspectiveWindowStatus}>
                      <span className={styles.livePulseDot} /> AES-256 Storage
                    </span>
                  </div>
                  <Shot
                    ratio="16x9"
                    label="Chat history & compliance audit"
                    src="/landing/Openline-chats.png"
                    alt="Openline chat history with multi-site filtering and sovereign compliance logs"
                    priority
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Voice & Video: Escalation Journey ───────────────── */}
      <section className={styles.section} id="video">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.kicker}>Voice &amp; Video</span>
            <h2 className={styles.sectionTitle}>Escalate to face-to-face in one click.</h2>
            <p className={styles.sectionSubtitle}>
              Direct peer-to-peer WebRTC voice and video calls with screenshare and local session recording — zero apps to install, zero external links.
            </p>
          </div>

          <div className={styles.escalationTimeline}>
            {/* Step 1 & 2: Visitor Previews */}
            <div className={styles.visitorEscalationRow}>
              <BorderGlow
                glowColor="#0979f5"
                borderRadius={16}
                backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
                className={styles.borderGlowCardWrap}
              >
                <div className={styles.visitorEscalationCard}>
                  <div className={styles.escalationCardHeader}>
                    <div className={styles.stepBadge}>Step 01</div>
                    <h3 className={styles.escalationCardTitle}>In-Chat Incoming Chime</h3>
                  </div>
                  <div className={styles.visitorFrame}>
                    <div className={styles.perspectiveWindowBar}>
                      <div className={styles.cockpitWindowControls}>
                        <span className={`${styles.windowDot} ${styles.dotClose}`} />
                        <span className={`${styles.windowDot} ${styles.dotMinimize}`} />
                        <span className={`${styles.windowDot} ${styles.dotExpand}`} />
                      </div>
                      <span className={styles.perspectiveWindowLabel}>Incoming Call Prompt</span>
                      <span className={styles.telemetryTag}>Direct WebRTC</span>
                    </div>
                    <div className={styles.visitorFrameMedia}>
                      <img
                        className={styles.visitorFrameImg}
                        src={`/landing/visitor-video-call-notification.png?v=${LANDING_IMG_VERSION}`}
                        alt="Visitor incoming video call notification in chat widget"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <p className={styles.escalationCardCaption}>
                    One gentle chime prompt on the visitor’s screen. No account creation or permissions wall.
                  </p>
                </div>
              </BorderGlow>

              <BorderGlow
                glowColor="#0979f5"
                borderRadius={16}
                backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
                className={styles.borderGlowCardWrap}
              >
                <div className={styles.visitorEscalationCard}>
                  <div className={styles.escalationCardHeader}>
                    <div className={styles.stepBadge}>Step 02</div>
                    <h3 className={styles.escalationCardTitle}>Active In-Page Call</h3>
                  </div>
                  <div className={styles.visitorFrame}>
                    <div className={styles.perspectiveWindowBar}>
                      <div className={styles.cockpitWindowControls}>
                        <span className={`${styles.windowDot} ${styles.dotClose}`} />
                        <span className={`${styles.windowDot} ${styles.dotMinimize}`} />
                        <span className={`${styles.windowDot} ${styles.dotExpand}`} />
                      </div>
                      <span className={styles.perspectiveWindowLabel}>Connected Call</span>
                      <span className={styles.telemetryTag}>Hardware Accelerated</span>
                    </div>
                    <div className={styles.visitorFrameMedia}>
                      <img
                        className={styles.visitorFrameImg}
                        src={`/landing/visitor-video-view.png?v=${LANDING_IMG_VERSION}`}
                        alt="Visitor in active WebRTC call with support agent"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <p className={styles.escalationCardCaption}>
                    Crystal-clear bi-directional audio/video rendered directly within the floating widget container.
                  </p>
                </div>
              </BorderGlow>
            </div>

            {/* Step 3: Agent Console Workstation */}
            <BorderGlow
              glowColor="#0979f5"
              borderRadius={16}
              backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
              className={styles.borderGlowCardWrap}
            >
              <div className={styles.agentEscalationBlock}>
                <div className={styles.agentEscalationHeader}>
                  <div className={styles.stepBadge}>Step 03</div>
                  <div>
                    <h3 className={styles.escalationCardTitle}>Agent Workstation &amp; Screenshare Studio</h3>
                    <p className={styles.escalationCardSub}>
                      High-resolution multi-tile video matrix with P2P screenshare and local recording
                    </p>
                  </div>
                </div>

                <div className={styles.agentScreensGrid}>
                  <div className={styles.agentScreenTile}>
                    <div className={styles.perspectiveWindowBar}>
                      <div className={styles.cockpitWindowControls}>
                        <span className={`${styles.windowDot} ${styles.dotClose}`} />
                        <span className={`${styles.windowDot} ${styles.dotMinimize}`} />
                        <span className={`${styles.windowDot} ${styles.dotExpand}`} />
                      </div>
                      <span className={styles.perspectiveWindowLabel}>
                        <MonitorPlay size={13} className={styles.accentText} /> Multi-Tile Video Matrix
                      </span>
                      <span className={styles.telemetryTag}>1080P WebRTC</span>
                    </div>
                    <Shot
                      ratio="16x9"
                      label="Agent live video call"
                      src="/landing/agent-video-view.png"
                      alt="Agent handling live multi-party video call with customer"
                    />
                  </div>

                  <div className={styles.agentScreenTile}>
                    <div className={styles.perspectiveWindowBar}>
                      <div className={styles.cockpitWindowControls}>
                        <span className={`${styles.windowDot} ${styles.dotClose}`} />
                        <span className={`${styles.windowDot} ${styles.dotMinimize}`} />
                        <span className={`${styles.windowDot} ${styles.dotExpand}`} />
                      </div>
                      <span className={styles.perspectiveWindowLabel}>
                        <MonitorPlay size={13} className={styles.accentText} /> Real-Time Screenshare Feed
                      </span>
                      <span className={styles.telemetryTag}>4K Screenshare</span>
                    </div>
                    <Shot
                      ratio="16x9"
                      label="Agent live screenshare studio"
                      src="/landing/agent-screenshare-view.png"
                      alt="Agent viewing customer screenshare alongside live notes and timeline"
                    />
                  </div>
                </div>
              </div>
            </BorderGlow>
          </div>
        </div>
      </section>

      {/* ── Customization & White-Labeling ─────────────────── */}
      <section className={styles.section} id="customize">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.kicker}>White-Label Studio</span>
            <h2 className={styles.sectionTitle}>Your brand down to the last pixel.</h2>
            <p className={styles.sectionSubtitle}>
              Customize colors, fonts, launcher icons, pre-chat capture, and model endpoints per site with zero code rebuilds.
            </p>
          </div>

          <div className={styles.customSpecsGrid}>
            {CUSTOMIZATION_CARDS.map(({ icon: Ic, title, note, tag }) => (
              <TiltedCard
                key={title}
                containerWidth="100%"
                containerHeight="100%"
                imageWidth="100%"
                imageHeight="100%"
                scaleOnHover={1.03}
                rotateAmplitude={10}
                showTooltip={false}
                glare={0.4}
                glareColor="#0979f5"
                radius={14}
                className={styles.tiltedCardWrapper}
              >
                <BorderGlow
                  glowColor="#0979f5"
                  borderRadius={14}
                  backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
                  className={styles.borderGlowCardWrap}
                >
                  <div className={styles.customSpecCard}>
                    <div className={styles.customSpecHeader}>
                      <div className={styles.customSpecIconWrap}>
                        <Ic size={18} />
                      </div>
                      <span className={styles.customSpecTag}>{tag}</span>
                    </div>
                    <h3 className={styles.customSpecTitle}>{title}</h3>
                    <p className={styles.customSpecNote}>{note}</p>
                  </div>
                </BorderGlow>
              </TiltedCard>
            ))}
          </div>

          <div className={styles.adminGallerySplit}>
            <BorderGlow
              glowColor="#0979f5"
              borderRadius={16}
              backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
              className={styles.borderGlowCardWrap}
            >
              <div className={styles.galleryFrame}>
                <div className={styles.perspectiveWindowBar}>
                  <div className={styles.cockpitWindowControls}>
                    <span className={`${styles.windowDot} ${styles.dotClose}`} />
                    <span className={`${styles.windowDot} ${styles.dotMinimize}`} />
                    <span className={`${styles.windowDot} ${styles.dotExpand}`} />
                  </div>
                  <span className={styles.perspectiveWindowLabel}>
                    <Palette size={13} className={styles.accentText} /> Widget &amp; Theme Studio
                  </span>
                  <span className={styles.telemetryTag}>Live Preview</span>
                </div>
                <Shot
                  ratio="16x9"
                  label="Site theme & widget customization"
                  src="/landing/Openline-site-admin.png"
                  alt="Openline site administration view for brand colors, fonts, and launcher settings"
                />
              </div>
            </BorderGlow>

            <BorderGlow
              glowColor="#0979f5"
              borderRadius={16}
              backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
              className={styles.borderGlowCardWrap}
            >
              <div className={styles.galleryFrame}>
                <div className={styles.perspectiveWindowBar}>
                  <div className={styles.cockpitWindowControls}>
                    <span className={`${styles.windowDot} ${styles.dotClose}`} />
                    <span className={`${styles.windowDot} ${styles.dotMinimize}`} />
                    <span className={`${styles.windowDot} ${styles.dotExpand}`} />
                  </div>
                  <span className={styles.perspectiveWindowLabel}>
                    <Webhook size={13} className={styles.accentText} /> Webhook &amp; Event Delivery Engine
                  </span>
                  <span className={styles.telemetryTag}>200 OK</span>
                </div>
                <Shot
                  ratio="16x9"
                  label="Webhooks & event delivery log"
                  src="/landing/Openline-webhook.png"
                  alt="Openline webhook payload template and real-time delivery logs"
                />
              </div>
            </BorderGlow>
          </div>
        </div>
      </section>

      {/* ── DevOps 60-Second Deploy & Terminal ─────────────── */}
      <section className={styles.deploySection} id="deploy">
        <div className={styles.container}>
          <div className={styles.deploySplit}>
            <div className={styles.deployOverview}>
              <span className={styles.kicker}>DevOps &amp; Infrastructure</span>
              <h2 className={styles.deployTitle}>Production deploy in 60 seconds.</h2>
              <p className={styles.deployLede}>
                Deploy a single Docker container or Helm chart. Zero external database dependencies required. Embed a single
                script tag on your website and own every single byte of your customer data.
              </p>

              <div className={styles.deployHighlightsList}>
                <AnimatedList
                  items={DEPLOY_ANIMATED_ITEMS}
                  theme={theme === 'dark' ? 'dark' : 'light'}
                  accentColor="#0979f5"
                  radius={12}
                  gap={12}
                  width="100%"
                  frame={false}
                  displayScrollbar={false}
                  initialSelectedIndex={0}
                  selectOnHover={true}
                />
              </div>
            </div>

            {/* Interactive Terminal */}
            <div className={styles.terminalContainer}>
              <div className={styles.terminalWindowBar}>
                <div className={styles.cockpitWindowControls}>
                  <span className={`${styles.windowDot} ${styles.dotClose}`} />
                  <span className={`${styles.windowDot} ${styles.dotMinimize}`} />
                  <span className={`${styles.windowDot} ${styles.dotExpand}`} />
                </div>
                <div className={styles.terminalTabs}>
                  <Button
                    variant={activeTerminalTab === 'docker' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={styles.terminalTabBtn}
                    data-active={activeTerminalTab === 'docker'}
                    onClick={() => setActiveTerminalTab('docker')}
                  >
                    docker-compose.yml
                  </Button>
                  <Button
                    variant={activeTerminalTab === 'helm' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={styles.terminalTabBtn}
                    data-active={activeTerminalTab === 'helm'}
                    onClick={() => setActiveTerminalTab('helm')}
                  >
                    Helm / K8s
                  </Button>
                  <Button
                    variant={activeTerminalTab === 'baremetal' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={styles.terminalTabBtn}
                    data-active={activeTerminalTab === 'baremetal'}
                    onClick={() => setActiveTerminalTab('baremetal')}
                  >
                    Bare-Metal Node
                  </Button>
                </div>
              </div>

              <div className={styles.terminalBody}>
                <div className={styles.terminalTopBar}>
                  <span className={styles.terminalFileLabel}>
                    {activeTerminalTab === 'docker' && 'docker-compose.yml'}
                    {activeTerminalTab === 'helm' && 'helm-values.yaml'}
                    {activeTerminalTab === 'baremetal' && 'bash script'}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className={styles.terminalCopyBtn}
                    onClick={() => handleCopyCommand(TERMINAL_COMMANDS[activeTerminalTab])}
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </Button>
                </div>
                <pre className={styles.terminalCode}>
                  <code>{TERMINAL_COMMANDS[activeTerminalTab]}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── shadcn/ui Architecture & Deployment Configurator ──────── */}
      <section className={styles.configuratorSection} id="configurator">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.kicker}>Interactive Architecture Studio</span>
            <h2 className={styles.sectionTitle}>Configure your sovereign cluster.</h2>
            <p className={styles.sectionSubtitle}>
              Customize deployment topology, zero-leak privacy controls, and local AI modules. Built entirely with authentic shadcn/ui components.
            </p>
          </div>

          <div className={styles.configuratorGrid}>
            {/* Left: Interactive Form with RadioGroup, Checkbox, Input, Textarea */}
            <Card className={styles.configuratorCard}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldCheck size={18} className="text-primary" />
                  Topology &amp; Security Controls
                </CardTitle>
                <CardDescription>
                  Choose where your data lives and toggle enterprise compliance modules.
                </CardDescription>
              </CardHeader>

              <CardContent className="flex flex-col gap-6">
                {/* RadioGroup: Deployment Topology */}
                <div className={styles.configuratorFormGroup}>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Deployment Topology (RadioGroup)
                  </Label>
                  <RadioGroup
                    value={deployTarget}
                    onValueChange={(v) => setDeployTarget(v as 'onprem' | 'vpc' | 'airgap')}
                  >
                    <RadioGroupItem
                      value="onprem"
                      id="radio-onprem"
                      label="On-Premises Bare Metal"
                      description="Physical rack or hypervisor with zero external gateway dependencies."
                    />
                    <RadioGroupItem
                      value="vpc"
                      id="radio-vpc"
                      label="Private Enterprise VPC (AWS / GCP / Azure)"
                      description="Dedicated private subnet behind internal reverse proxy and IAM boundary."
                    />
                    <RadioGroupItem
                      value="airgap"
                      id="radio-airgap"
                      label="Air-Gapped Sovereign Cluster"
                      description="Strict classified/healthcare network with zero DNS egress and local storage."
                    />
                  </RadioGroup>
                </div>

                <Separator />

                {/* Checkbox Group: Compliance & Capabilities */}
                <div className={styles.configuratorFormGroup}>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Sovereign Modules &amp; Compliance (Checkboxes)
                  </Label>
                  <div className={styles.configuratorCheckboxes}>
                    <Checkbox
                      id="mod-llm"
                      checked={cfgModules.privateLlm}
                      onCheckedChange={(c) => setCfgModules((m) => ({ ...m, privateLlm: !!c }))}
                      label="Private Self-Hosted LLM"
                      description="Local Ollama / vLLM on enterprise GPUs"
                    />
                    <Checkbox
                      id="mod-webrtc"
                      checked={cfgModules.webrtcVideo}
                      onCheckedChange={(c) => setCfgModules((m) => ({ ...m, webrtcVideo: !!c }))}
                      label="WebRTC Voice & Video"
                      description="Direct P2P audio & multi-tile video"
                    />
                    <Checkbox
                      id="mod-hipaa"
                      checked={cfgModules.hipaaVault}
                      onCheckedChange={(c) => setCfgModules((m) => ({ ...m, hipaaVault: !!c }))}
                      label="HIPAA Audit Vault"
                      description="Tamper-evident logs & AES-256 at rest"
                    />
                    <Checkbox
                      id="mod-screenshare"
                      checked={cfgModules.screenshare}
                      onCheckedChange={(c) => setCfgModules((m) => ({ ...m, screenshare: !!c }))}
                      label="4K Bi-Directional Screenshare"
                      description="Hardware-accelerated peer sharing"
                    />
                    <Checkbox
                      id="mod-whitelabel"
                      checked={cfgModules.whiteLabel}
                      onCheckedChange={(c) => setCfgModules((m) => ({ ...m, whiteLabel: !!c }))}
                      label="White-Label Theme Engine"
                      description="Complete brand token customization"
                    />
                    <Checkbox
                      id="mod-webhook"
                      checked={cfgModules.webhookRelay}
                      onCheckedChange={(c) => setCfgModules((m) => ({ ...m, webhookRelay: !!c }))}
                      label="Enterprise Webhook Relay"
                      description="Zero-leak internal bus webhook dispatch"
                    />
                  </div>
                </div>

                <Separator />

                {/* Inputs: Work Email & Domain */}
                <div className={styles.configuratorFormRow}>
                  <div className={styles.configuratorFormGroup}>
                    <Label htmlFor="cfg-email">Work Email (Input)</Label>
                    <Input
                      id="cfg-email"
                      type="email"
                      placeholder="devops@enterprise.corp"
                      value={cfgEmail}
                      onChange={(e) => setCfgEmail(e.target.value)}
                    />
                  </div>
                  <div className={styles.configuratorFormGroup}>
                    <Label htmlFor="cfg-domain">Target Domain / Hostname (Input)</Label>
                    <Input
                      id="cfg-domain"
                      type="text"
                      placeholder="support.company.internal"
                      value={cfgDomain}
                      onChange={(e) => setCfgDomain(e.target.value)}
                    />
                  </div>
                </div>

                {/* Textarea: Infrastructure Requirements */}
                <div className={styles.configuratorFormGroup}>
                  <Label htmlFor="cfg-notes">Special Compliance &amp; Hardware Notes (Textarea)</Label>
                  <Textarea
                    id="cfg-notes"
                    rows={3}
                    placeholder="E.g. NVIDIA A100 GPU cluster available, requires BAA signature, Kubernetes v1.30 ingress..."
                    value={cfgNotes}
                    onChange={(e) => setCfgNotes(e.target.value)}
                  />
                </div>
              </CardContent>

              <CardFooter className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className={styles.livePulseDot} />
                  <span>{activeModuleCount} modules enabled · 100% On-Premise</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setCfgModules({
                        privateLlm: true,
                        webrtcVideo: true,
                        hipaaVault: true,
                        screenshare: true,
                        whiteLabel: true,
                        webhookRelay: true,
                      });
                    }}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCfgEmail('');
                      setCfgDomain('support.enterprise.corp');
                      setCfgNotes('');
                    }}
                  >
                    Reset
                  </Button>
                  <Button variant="default" size="sm" onClick={startChat}>
                    <span>Verify Config</span>
                    <ArrowRight size={14} className="ml-1" />
                  </Button>
                </div>
              </CardFooter>
            </Card>

            {/* Right: Live Generated Spec Preview with Tabs & Badges */}
            <div className={styles.configuratorOutputCard}>
              <div className={styles.configuratorOutputHead}>
                <div className={styles.configuratorOutputTitle}>
                  <Terminal size={16} className="text-primary" />
                  <span>Generated Architecture Spec</span>
                </div>
                <Badge variant={deployTarget === 'airgap' ? 'destructive' : deployTarget === 'onprem' ? 'success' : 'default'}>
                  {deployTarget === 'airgap' ? 'Air-Gapped' : deployTarget === 'onprem' ? 'Bare-Metal' : 'Private VPC'}
                </Badge>
              </div>

              <div className={styles.configuratorBadgeList}>
                <Badge variant="outline">MIT Licensed</Badge>
                <Badge variant="secondary">Zero Telemetry</Badge>
                {cfgModules.hipaaVault && <Badge variant="default">HIPAA Compliant</Badge>}
                {cfgModules.privateLlm && <Badge variant="outline">Local Ollama</Badge>}
                {cfgModules.webrtcVideo && <Badge variant="secondary">P2P WebRTC</Badge>}
              </div>

              <Tabs value={activeManifestTab} onValueChange={(v) => setActiveManifestTab(v as 'compose' | 'k8s' | 'env')}>
                <TabsList className="w-full grid grid-cols-3">
                  <TabsTrigger value="compose">docker-compose.yml</TabsTrigger>
                  <TabsTrigger value="k8s">kubernetes.yaml</TabsTrigger>
                  <TabsTrigger value="env">openline.env</TabsTrigger>
                </TabsList>

                <TabsContent value={activeManifestTab} className="mt-3">
                  <pre className={styles.configuratorCodeBlock}>
                    <code>{generateManifestText()}</code>
                  </pre>
                </TabsContent>
              </Tabs>

              <div className="flex items-center justify-between gap-3 pt-2">
                <Button
                  variant="default"
                  size="sm"
                  className="w-full flex items-center justify-center gap-2"
                  onClick={handleCopyManifest}
                >
                  {specCopied ? <Check size={14} /> : <Copy size={14} />}
                  <span>{specCopied ? 'Manifest Copied to Clipboard!' : 'Copy Deployment Manifest'}</span>
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center m-0">
                Ready to deploy. Run directly on your infrastructure with zero external telemetry.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Open Source (MIT) Band ──────────────────────────── */}
      <section className={styles.openSourceSection} id="open-source">
        <div className={styles.container}>
          <BorderGlow
            glowColor="#0979f5"
            borderRadius={22}
            backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
            className={styles.borderGlowCardWrap}
          >
            <div className={styles.openSourceCard}>
              <div className={styles.openSourceContent}>
                <span className={styles.kicker}>Open Source (MIT)</span>
                <h2 className={styles.openSourceTitle}>Free to self-host. Yours to audit.</h2>
                <p className={styles.openSourceText}>
                  Openline is 100% open source under the permissive MIT license. Every line of code for the live chat,
                  private AI engine, WebRTC video calling, and agent console lives openly on GitHub. Run it yourself at zero cost,
                  or partner with our team for managed enterprise deployment and custom compliance SLAs.
                </p>
                <div className={styles.openSourceActions}>
                  <SpecularButton
                    size="md"
                    radius={9999}
                    tint="#0979f5"
                    tintOpacity={0.92}
                    textColor="#ffffff"
                    lineColor="#ffffff"
                    baseColor="#0660c7"
                    intensity={1.25}
                    idleIntensity={0.4}
                    glow={1.2}
                    onClick={startChat}
                    className={styles.heroSpecularBtn}
                  >
                    <span>Get managed deployment</span>
                    <ArrowRight size={16} aria-hidden="true" />
                  </SpecularButton>
                </div>
              </div>
            </div>
          </BorderGlow>
        </div>
      </section>

      {/* ── FAQ Section (2-Column Liquid Accordion) ─────────── */}
      <section className={styles.faqSection} id="faq">
        <div className={styles.container}>
          <div className={styles.faqLayout}>
            {/* Left Column: Sticky Editorial Heading & Support Help */}
            <div className={styles.faqSideCol}>
              <span className={styles.kicker}>FAQ</span>
              <h2 className={styles.sectionTitle} style={{ textAlign: 'left', marginTop: 12 }}>
                Questions we keep getting.
              </h2>
              <p className={styles.faqSideLede}>
                Everything you need to know about sovereign hosting, HIPAA compliance, air-gapped AI models, and white-labeling.
              </p>

              <BorderGlow
                glowColor="#0979f5"
                borderRadius={14}
                backgroundColor={theme === 'dark' ? 'rgba(14, 16, 23, 0.88)' : 'rgba(255, 255, 255, 0.95)'}
                className={styles.borderGlowCardWrap}
              >
                <div className={styles.faqHelpCard}>
                  <h4 className={styles.faqHelpTitle}>Have custom infrastructure needs?</h4>
                  <p className={styles.faqHelpText}>
                    Our engineering team can assist with custom Kubernetes manifests, VPC peering, and enterprise compliance audits.
                  </p>
                  <Button variant="outline" size="sm" className={styles.faqHelpBtn} onClick={startChat}>
                    <span>Ask an engineer live</span>
                    <ArrowRight size={14} />
                  </Button>
                </div>
              </BorderGlow>
            </div>

            {/* Right Column: Liquid Smooth Accordion Stream */}
            <div className={styles.faqStream}>
              {FAQ_ITEMS.map((item, i) => {
                const open = openFaq === i;
                const indexStr = String(i + 1).padStart(2, '0');
                return (
                  <div key={item.q} className={styles.faqCard} data-open={open}>
                    <button
                      type="button"
                      className={styles.faqBtn}
                      aria-expanded={open}
                      onClick={() => setOpenFaq(open ? null : i)}
                    >
                      <div className={styles.faqQuestionWrap}>
                        <span className={styles.faqIndex}>{indexStr}</span>
                        <span className={styles.faqQ}>{item.q}</span>
                      </div>
                      <span className={styles.faqToggleIcon} aria-hidden="true">
                        <ChevronDown size={18} />
                      </span>
                    </button>
                    <div className={styles.faqAnswerWrapper} aria-hidden={!open}>
                      <div className={styles.faqAnswerInner}>
                        <p className={styles.faqAnswerBody}>{item.a}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── Final Executive CTA ─────────────────────────────── */}
      <section className={styles.ctaSection} id="contact">
        <div className={styles.container}>
          <div className={styles.ctaBox}>
            <div className={styles.ctaAura} aria-hidden="true" />
            <div className={styles.ctaInner}>
              <span className={styles.kicker}>Get started today</span>
              <h2 className={styles.ctaHeading}>
                Talk to us — or start chatting on this page.
              </h2>
              <p className={styles.ctaLede}>
                See Openline running live on this very website. Click below to launch the widget, or reach out to our team
                to schedule a sovereign architecture briefing.
              </p>

              <div className={styles.ctaActions}>
                <SpecularButton
                  size="md"
                  radius={9999}
                  tint="#0979f5"
                  tintOpacity={0.92}
                  textColor="#ffffff"
                  lineColor="#ffffff"
                  baseColor="#0660c7"
                  intensity={1.25}
                  idleIntensity={0.4}
                  glow={1.2}
                  onClick={startChat}
                  className={styles.heroSpecularBtn}
                >
                  <span>Start live chat</span>
                  <ArrowRight size={16} aria-hidden="true" />
                </SpecularButton>
                <Button variant="outline" size="lg" asChild className={styles.secondaryBtn}>
                  <a href={CONTACT_URL}>
                    <span>Contact engineering team</span>
                    <ArrowUpRight size={16} aria-hidden="true" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────── */}
      <footer className={styles.footer}>
        <div className={styles.container}>
          <div className={styles.footerGrid}>
            <div className={styles.footerBrandCol}>
              <BrandLogo variant="wordmark" height={24} />
              <p className={styles.footerBrandText}>
                The open-source, self-hosted customer communication platform with private AI, WebRTC video, and zero third parties.
              </p>
              <div className={styles.footerStatusIndicator}>
                <span className={styles.livePulseDot} />
                <span>All systems sovereign · MIT License</span>
              </div>
            </div>

            <div className={styles.footerNavCol}>
              <h4 className={styles.footerColTitle}>Architecture &amp; Features</h4>
              <ul className={styles.footerList}>
                <li><a className={styles.footerLink} href="#architecture" onClick={scrollToSection('architecture')}>Wire security</a></li>
                <li><a className={styles.footerLink} href="#experience" onClick={scrollToSection('experience')}>Platform experience</a></li>
                <li><a className={styles.footerLink} href="#video" onClick={scrollToSection('video')}>Voice &amp; video</a></li>
                <li><a className={styles.footerLink} href="#customize" onClick={scrollToSection('customize')}>White-label studio</a></li>
                <li><a className={styles.footerLink} href="#deploy" onClick={scrollToSection('deploy')}>DevOps deploy</a></li>
              </ul>
            </div>

            <div className={styles.footerNavCol}>
              <h4 className={styles.footerColTitle}>Resources &amp; Code</h4>
              <ul className={styles.footerList}>
                <li><a className={styles.footerLink} href="#faq" onClick={scrollToSection('faq')}>FAQ</a></li>
                <li>
                  <a className={styles.footerLink} href={GITHUB_URL} target="_blank" rel="noreferrer">
                    GitHub repository
                  </a>
                </li>
                <li><a className={styles.footerLink} href={CONTACT_URL}>Contact sales</a></li>
                <li><Link className={styles.footerLink} to={signInTo}>{signInLabel}</Link></li>
                {!isAuthed && <li><Link className={styles.footerLink} to="/signup">Create an account</Link></li>}
              </ul>
            </div>
          </div>

          <div className={styles.footerBottomBar}>
            <span>© {new Date().getFullYear()} Prakash Software Solutions Pvt Ltd. · Openline</span>
            <div className={styles.footerBottomLinks}>
              <span>100% Data Sovereignty</span>
              <span>·</span>
              <span>Zero Cloud Telemetry</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Bottom Navigation Dock */}
      <Dock items={dockItems} />
    </div>
  );
}
